import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Checkbox,
  Divider,
  Alert,
  Empty,
  Tooltip,
  Steps,
  Switch,
  Transfer,
  List,
  Avatar,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  UserOutlined,
  BankOutlined,
  SafetyOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import api from "../services/api";
import LoadingSpinner from "../components/UI/LoadingSpinner";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

const MultiCompanyAdmin = () => {
  const [admins, setAdmins] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [form] = Form.useForm();
  const [permissionForm] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [originalCompanyIds, setOriginalCompanyIds] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("admins");

  // Patrol state
  const [patrolUsers, setPatrolUsers] = useState([]);
  const [availableGuards, setAvailableGuards] = useState([]);
  const [patrolModalVisible, setPatrolModalVisible] = useState(false);
  const [patrolForm] = Form.useForm();
  const [patrolCreating, setPatrolCreating] = useState(false);

  // Subordinate assignment state
  const [subordinateModalVisible, setSubordinateModalVisible] = useState(false);
  const [editingSupervisor, setEditingSupervisor] = useState(null);
  const [allGuardsForAssignment, setAllGuardsForAssignment] = useState([]);
  const [selectedGuardIds, setSelectedGuardIds] = useState([]);
  const [subordinateSaving, setSubordinateSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [adminsRes, companiesRes, patrolRes, guardsRes] = await Promise.all([
        api.get("/multi-company/multi-admins"),
        api.get("/multi-company/companies"),
        api.get("/multi-company/multi-patrols"),
        api.get("/multi-company/available-guards"),
      ]);

      if (adminsRes.data.success) setAdmins(adminsRes.data.data);
      if (companiesRes.data.success) setCompanies(companiesRes.data.data);
      if (patrolRes.data.success) setPatrolUsers(patrolRes.data.data);
      if (guardsRes.data.success) setAvailableGuards(guardsRes.data.data);
    } catch (error) {
      console.error("Fetch error:", error);
      message.error("ไม่สามารถโหลดข้อมูลได้");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAdmin(null);
    form.resetFields();
    setCurrentStep(0);
    setSelectedCompanies([]);
    setOriginalCompanyIds([]);
    setModalVisible(true);
  };

  const handleEdit = async (admin) => {
    try {
      // ดึงข้อมูลสิทธิ์ของ admin คนนี้
      const response = await api.get(
        `/multi-company/multi-admins/${admin.id}/permissions`,
      );
      if (response.data.success) {
        setEditingAdmin(admin);
        form.setFieldsValue({
          name: admin.name,
          email: admin.email,
          primary_company_id: admin.company_id,
        });

        // ตั้งค่าสิทธิ์บริษัท
        const permissions = response.data.data.map((p) => ({
          company_id: p.company_id,
          company_name:
            p.company_name ||
            companies.find((c) => c.id === p.company_id)?.name ||
            "ไม่ทราบชื่อ",
          access_level: p.access_level,
          can_manage_users: p.can_manage_users,
          can_manage_checkpoints: p.can_manage_checkpoints,
          can_manage_rounds: p.can_manage_rounds,
          can_view_reports: p.can_view_reports,
          can_export_data: p.can_export_data,
        }));
        setSelectedCompanies(permissions);
        setOriginalCompanyIds(permissions.map((p) => p.company_id));
        setCurrentStep(0);
        setModalVisible(true);
      }
    } catch (error) {
      console.error("Fetch admin details error:", error);
      message.error("ไม่สามารถโหลดข้อมูลผู้ดูแลระบบได้");
    }
  };

  const handleViewPermissions = async (admin) => {
    try {
      const response = await api.get(
        `/multi-company/multi-admins/${admin.id}/permissions`,
      );
      if (response.data.success) {
        setEditingAdmin(admin);
        setSelectedCompanies(response.data.data);
        setPermissionModalVisible(true);
      }
    } catch (error) {
      console.error("Fetch permissions error:", error);
      message.error("ไม่สามารถโหลดข้อมูลสิทธิ์ได้");
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        email: values.email,
        password: values.password,
        primary_company_id:
          selectedCompanies.length > 0 ? selectedCompanies[0].company_id : null,
        company_permissions: selectedCompanies,
      };

      if (editingAdmin) {
        await api.put(
          `/multi-company/multi-admins/${editingAdmin.id}/permissions`,
          {
            company_permissions: selectedCompanies,
          },
        );
        message.success("อัปเดตสิทธิ์ผู้ดูแลระบบสำเร็จ!");
      } else {
        await api.post("/multi-company/multi-admins", payload);
        message.success("สร้างผู้ดูแลระบบหลายบริษัทสำเร็จ!");
      }

      setModalVisible(false);
      fetchData();
    } catch (error) {
      console.error("Submit error:", error);
      message.error(error.response?.data?.message || "การบันทึกล้มเหลว");
    }
  };

  const handleRevokeCompanyAccess = async (adminId, companyId) => {
    try {
      await api.delete(
        `/multi-company/multi-admins/${adminId}/companies/${companyId}`,
      );
      message.success("เพิกถอนสิทธิ์การเข้าถึงบริษัทสำเร็จ!");
      setSelectedCompanies((prev) =>
        prev.filter((sc) => sc.company_id !== companyId),
      );
      fetchData();
    } catch (error) {
      console.error("Revoke access error:", error);
      message.error("การเพิกถอนสิทธิ์ล้มเหลว");
    }
  };

  const nextStep = () => {
    if (currentStep === 0) {
      const fieldsToValidate = editingAdmin
        ? ["name", "email"]
        : ["name", "email", "password"];
      form
        .validateFields(fieldsToValidate)
        .then(() => {
          if (selectedCompanies.length === 0) {
            message.warning("กรุณาเลือกอย่างน้อย 1 บริษัท");
            return;
          }
          setCurrentStep(currentStep + 1);
        })
        .catch((info) => {
          console.log("Validate Failed:", info);
        });
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const accessLevelPresets = {
    full: {
      can_manage_users: true,
      can_manage_checkpoints: true,
      can_manage_rounds: true,
      can_view_reports: true,
      can_export_data: true,
    },
    limited: {
      can_manage_users: false,
      can_manage_checkpoints: false,
      can_manage_rounds: false,
      can_view_reports: true,
      can_export_data: true,
    },
    read_only: {
      can_manage_users: false,
      can_manage_checkpoints: false,
      can_manage_rounds: false,
      can_view_reports: true,
      can_export_data: false,
    },
  };

  const updateCompanyPermission = (companyId, field, value) => {
    setSelectedCompanies((prev) =>
      prev.map((comp) => {
        if (comp.company_id !== companyId) return comp;

        // ถ้าเปลี่ยน access_level → auto set checkboxes ตาม preset
        if (field === "access_level" && accessLevelPresets[value]) {
          return { ...comp, access_level: value, ...accessLevelPresets[value] };
        }

        // ถ้าเปลี่ยน checkbox → auto update access_level ให้ตรง
        const updated = { ...comp, [field]: value };
        if (
          updated.can_manage_users &&
          updated.can_manage_checkpoints &&
          updated.can_manage_rounds &&
          updated.can_view_reports &&
          updated.can_export_data
        ) {
          updated.access_level = "full";
        } else if (
          !updated.can_manage_users &&
          !updated.can_manage_checkpoints &&
          !updated.can_manage_rounds &&
          updated.can_view_reports &&
          !updated.can_export_data
        ) {
          updated.access_level = "read_only";
        } else {
          updated.access_level = "limited";
        }
        return updated;
      }),
    );
  };

  const addCompanyPermission = (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    if (
      company &&
      !selectedCompanies.find((sc) => sc.company_id === companyId)
    ) {
      setSelectedCompanies((prev) => [
        ...prev,
        {
          company_id: companyId,
          company_name: company.name,
          access_level: "full",
          can_manage_users: true,
          can_manage_checkpoints: true,
          can_manage_rounds: true,
          can_view_reports: true,
          can_export_data: true,
        },
      ]);
    }
  };

  const removeCompanyPermission = (companyId) => {
    setSelectedCompanies((prev) =>
      prev.filter((comp) => comp.company_id !== companyId),
    );
  };

  // ==================== Patrol Functions ====================

  const handleCreatePatrol = () => {
    patrolForm.resetFields();
    setPatrolModalVisible(true);
  };

  const handleSubmitPatrol = async (values) => {
    try {
      setPatrolCreating(true);
      const res = await api.post("/multi-company/multi-patrols", {
        user_id: values.user_id,
        email: values.email,
      });
      if (res.data.success) {
        message.success(res.data.message);
        setPatrolModalVisible(false);
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || "การสร้างล้มเหลว");
    } finally {
      setPatrolCreating(false);
    }
  };

  // ==================== Subordinate Functions ====================

  const handleOpenSubordinates = async (supervisor) => {
    setEditingSupervisor(supervisor);
    setSubordinateModalVisible(true);

    try {
      const [subsRes, guardsRes] = await Promise.all([
        api.get(`/multi-company/patrol-subordinates/${supervisor.id}`),
        api.get("/multi-company/all-guards-for-assignment"),
      ]);

      if (subsRes.data.success) {
        setSelectedGuardIds(subsRes.data.data.map((s) => s.guard_user_id));
      }
      if (guardsRes.data.success) {
        setAllGuardsForAssignment(guardsRes.data.data);
      }
    } catch (error) {
      message.error("ไม่สามารถโหลดข้อมูลได้");
    }
  };

  const handleSaveSubordinates = async () => {
    if (!editingSupervisor) return;

    try {
      setSubordinateSaving(true);
      const res = await api.post("/multi-company/patrol-subordinates", {
        supervisor_user_id: editingSupervisor.id,
        guard_user_ids: selectedGuardIds,
      });
      if (res.data.success) {
        message.success(res.data.message);
        setSubordinateModalVisible(false);
        fetchData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || "การบันทึกล้มเหลว");
    } finally {
      setSubordinateSaving(false);
    }
  };

  const patrolColumns = [
    {
      title: "ชื่อ",
      key: "name",
      render: (_, record) => (
        <Space>
          <Avatar icon={<TeamOutlined />} style={{ backgroundColor: "#1890ff" }} />
          <div>
            <Text strong>{record.name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              PIN: {record.employee_id || "-"}
            </Text>
          </div>
        </Space>
      ),
      width: 220,
    },
    {
      title: "อีเมล",
      dataIndex: "email",
      key: "email",
      width: 220,
    },
    {
      title: "บริษัทเดิม",
      dataIndex: "company_name",
      key: "company_name",
      width: 200,
    },
    {
      title: "ผู้ใต้บังคับบัญชา",
      dataIndex: "subordinate_count",
      key: "subordinate_count",
      render: (count) => (
        <Tag color={count > 0 ? "blue" : "default"}>
          {count || 0} คน
        </Tag>
      ),
      width: 120,
      align: "center",
    },
    {
      title: "สถานะ",
      key: "status",
      render: (_, record) => (
        <Tag color={record.is_active ? "success" : "default"}>
          {record.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน"}
        </Tag>
      ),
      width: 100,
      align: "center",
    },
    {
      title: "จัดการ",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<TeamOutlined />}
          onClick={() => handleOpenSubordinates(record)}
        >
          กำหนดผู้ใต้บังคับบัญชา
        </Button>
      ),
      width: 200,
    },
  ];

  const columns = [
    {
      title: "ผู้ดูแลระบบ",
      key: "admin_info",
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Space>
            <Avatar icon={<UserOutlined />} />
            <div>
              <Text strong style={{ fontSize: 16 }}>
                {record.name}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 13 }}>
                {record.email}
              </Text>
            </div>
          </Space>
        </Space>
      ),
      width: 250,
    },
    {
      title: "บริษัทหลัก",
      dataIndex: "primary_company_name",
      key: "primary_company",
      width: 200,
    },
    {
      title: "บริษัทที่สามารถเข้าถึง",
      key: "accessible_companies",
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Text strong>{record.company_count} บริษัท</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.accessible_companies || "ไม่มีสิทธิ์เข้าถึง"}
          </Text>
        </Space>
      ),
      width: 300,
    },
    {
      title: "สถานะ",
      key: "status",
      render: (_, record) => (
        <Tag color={record.is_active ? "success" : "default"}>
          {record.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน"}
        </Tag>
      ),
      width: 100,
      align: "center",
    },
    {
      title: "การจัดการ",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="ดูสิทธิ์">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewPermissions(record)}
            />
          </Tooltip>
          <Tooltip title="แก้ไขสิทธิ์">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
        </Space>
      ),
      width: 120,
      align: "center",
    },
  ];

  const stepItems = [
    {
      title: "ข้อมูลพื้นฐาน",
      icon: <UserOutlined />,
      description: "ข้อมูลผู้ใช้และบริษัทหลัก",
    },
    {
      title: "สิทธิ์บริษัท",
      icon: <BankOutlined />,
      description: "เลือกบริษัทและกำหนดสิทธิ์",
    },
    {
      title: "ตรวจสอบ",
      icon: <CheckCircleOutlined />,
      description: "ตรวจสอบข้อมูลก่อนบันทึก",
    },
  ];

  if (loading) {
    return <LoadingSpinner tip="กำลังโหลดข้อมูล..." />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title
            level={2}
            style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
          >
            <SafetyOutlined style={{ color: "#1890ff" }} />
            จัดการผู้ดูแลระบบหลายบริษัท
          </Title>
          <Paragraph
            type="secondary"
            style={{ margin: "8px 0 0 0", fontSize: 14 }}
          >
            ผู้ดูแลระบบที่สามารถจัดการหลายบริษัทพร้อมกันตามสิทธิ์ที่กำหนด
          </Paragraph>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "admins",
            label: (
              <span>
                <SafetyOutlined /> ผู้ดูแลระบบ
              </span>
            ),
            children: (
              <>
                <Row justify="end" style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                    size="large"
                  >
                    สร้างผู้ดูแลระบบใหม่
                  </Button>
                </Row>

                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={8}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <Title level={3} style={{ color: "#52c41a", margin: "0 0 8px 0" }}>
                          {admins.filter((a) => a.is_active).length}
                        </Title>
                        <Text type="secondary">ผู้ดูแลที่เปิดใช้งาน</Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <Title level={3} style={{ color: "#1890ff", margin: "0 0 8px 0" }}>
                          {companies.length}
                        </Title>
                        <Text type="secondary">บริษัททั้งหมด</Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <Title level={3} style={{ color: "#fa8c16", margin: "0 0 8px 0" }}>
                          {admins.reduce((sum, a) => sum + (a.company_count || 0), 0)}
                        </Title>
                        <Text type="secondary">การเข้าถึงทั้งหมด</Text>
                      </div>
                    </Card>
                  </Col>
                </Row>

                <Card>
                  {admins.length > 0 ? (
                    <Table
                      columns={columns}
                      dataSource={admins}
                      rowKey="id"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} จาก ${total} ผู้ดูแลระบบ`,
                      }}
                    />
                  ) : (
                    <Empty
                      description="ยังไม่มีผู้ดูแลระบบหลายบริษัท"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        สร้างผู้ดูแลระบบใหม่
                      </Button>
                    </Empty>
                  )}
                </Card>
              </>
            ),
          },
          {
            key: "patrol",
            label: (
              <span>
                <TeamOutlined /> ผู้จัดการ รปภ
              </span>
            ),
            children: (
              <>
                <Row justify="end" style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreatePatrol}
                    size="large"
                  >
                    สร้างผู้จัดการ รปภ
                  </Button>
                </Row>

                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={12}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <Title level={3} style={{ color: "#1890ff", margin: "0 0 8px 0" }}>
                          {patrolUsers.length}
                        </Title>
                        <Text type="secondary">ผู้จัดการ รปภ ที่เปิดใช้งาน</Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <Title level={3} style={{ color: "#52c41a", margin: "0 0 8px 0" }}>
                          {availableGuards.length}
                        </Title>
                        <Text type="secondary">รปภ ที่พร้อม Promote</Text>
                      </div>
                    </Card>
                  </Col>
                </Row>

                <Card>
                  {patrolUsers.length > 0 ? (
                    <Table
                      columns={patrolColumns}
                      dataSource={patrolUsers}
                      rowKey="id"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} จาก ${total} ผู้จัดการ รปภ`,
                      }}
                    />
                  ) : (
                    <Empty
                      description="ยังไม่มีผู้จัดการ รปภ"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleCreatePatrol}>
                        สร้างผู้จัดการ รปภ
                      </Button>
                    </Empty>
                  )}
                </Card>
              </>
            ),
          },
        ]}
      />

      {/* Modal สร้าง/แก้ไขผู้ดูแลระบบ */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            {editingAdmin
              ? "แก้ไขผู้ดูแลระบบหลายบริษัท"
              : "สร้างผู้ดูแลระบบหลายบริษัท"}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Steps
          current={currentStep}
          items={stepItems}
          style={{ marginBottom: 32 }}
        />

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {currentStep === 0 && (
            <div>
              <Alert
                message="ข้อมูลพื้นฐานของผู้ดูแลระบบ"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="ชื่อ-นามสกุล"
                    rules={[
                      { required: true, message: "กรุณาระบุชื่อ-นามสกุล" },
                    ]}
                  >
                    <Input placeholder="ชื่อ-นามสกุล" size="large" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    label="อีเมล"
                    rules={[
                      { required: true, message: "กรุณาระบุอีเมล" },
                      { type: "email", message: "รูปแบบอีเมลไม่ถูกต้อง" },
                    ]}
                  >
                    <Input placeholder="email@company.com" size="large" />
                  </Form.Item>
                </Col>
              </Row>

              {!editingAdmin && (
                <Form.Item
                  name="password"
                  label="รหัสผ่าน"
                  rules={[
                    { required: true, message: "กรุณาระบุรหัสผ่าน" },
                    { min: 6, message: "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร" },
                  ]}
                >
                  <Input.Password placeholder="รหัสผ่าน" size="large" />
                </Form.Item>
              )}

              <Form.Item
                label="เลือกบริษัทที่ดูแล"
                required
                validateStatus={selectedCompanies.length === 0 ? "error" : ""}
                help={
                  selectedCompanies.length === 0
                    ? "กรุณาเลือกอย่างน้อย 1 บริษัท"
                    : ""
                }
              >
                <Transfer
                  dataSource={companies.map(c => ({
                    key: c.id,
                    title: c.name,
                    disabled: originalCompanyIds.includes(c.id) // Protect existing assignments
                  }))}
                  titles={['บริษัททั้งหมด', 'บริษัทที่เลือก']}
                  targetKeys={selectedCompanies.map((sc) => sc.company_id)}
                  onChange={(nextTargetKeys, direction, moveKeys) => {
                    const selectedIds = nextTargetKeys;

                    // ป้องกันการลบบริษัทที่มีสิทธิ์อยู่แล้ว (Transfer disabled prop handles UI, this handles logic)
                    if (direction === 'left') {
                      const removedOriginals = moveKeys.filter(key => originalCompanyIds.includes(key));
                      if (removedOriginals.length > 0) {
                        message.warning("ไม่สามารถลบบริษัทที่กำหนดสิทธิ์ไว้แล้วได้ กรุณาไปเพิกถอนสิทธิ์ก่อน");
                        return;
                      }
                    }

                    // เพิ่มบริษัทใหม่
                    selectedIds.forEach((id) => {
                      if (!selectedCompanies.find((sc) => sc.company_id === id)) {
                        addCompanyPermission(id);
                      }
                    });

                    // ลบบริษัทที่ถูกเอาออก
                    setSelectedCompanies((prev) =>
                      prev.filter((sc) => selectedIds.includes(sc.company_id)),
                    );
                  }}
                  render={item => item.title}
                  listStyle={{
                    width: '100%',
                    height: 300,
                  }}
                  showSearch
                  searchPlaceholder="ค้นหาบริษัท"
                  operations={['เพิ่ม', 'นำออก']}
                />
              </Form.Item>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Alert
                message="กำหนดสิทธิ์การเข้าถึงบริษัท"
                description="กำหนดสิทธิ์ที่ผู้ดูแลระบบสามารถทำได้ในแต่ละบริษัท (เพิ่ม/ลบบริษัทได้ที่ขั้นตอนก่อนหน้า)"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                {selectedCompanies.map((company, index) => (
                  <Card
                    key={company.company_id}
                    size="small"
                    style={{ marginBottom: 16 }}
                  >
                    <Row gutter={16} align="middle">
                      <Col span={6}>
                        <Text strong>{company.company_name}</Text>
                        <br />
                        <Select
                          value={company.access_level}
                          size="small"
                          style={{ width: "100%", marginTop: 4 }}
                          onChange={(value) =>
                            updateCompanyPermission(
                              company.company_id,
                              "access_level",
                              value,
                            )
                          }
                        >
                          <Option value="full">สิทธิ์เต็ม</Option>
                          <Option value="limited">สิทธิ์จำกัด</Option>
                          <Option value="read_only">อ่านอย่างเดียว</Option>
                        </Select>
                      </Col>
                      <Col span={16}>
                        <Row gutter={[8, 8]}>
                          <Col span={12}>
                            <Checkbox
                              checked={company.can_manage_users}
                              onChange={(e) =>
                                updateCompanyPermission(
                                  company.company_id,
                                  "can_manage_users",
                                  e.target.checked,
                                )
                              }
                            >
                              จัดการผู้ใช้
                            </Checkbox>
                          </Col>
                          <Col span={12}>
                            <Checkbox
                              checked={company.can_manage_checkpoints}
                              onChange={(e) =>
                                updateCompanyPermission(
                                  company.company_id,
                                  "can_manage_checkpoints",
                                  e.target.checked,
                                )
                              }
                            >
                              จัดการจุดตรวจ
                            </Checkbox>
                          </Col>
                          <Col span={12}>
                            <Checkbox
                              checked={company.can_manage_rounds}
                              onChange={(e) =>
                                updateCompanyPermission(
                                  company.company_id,
                                  "can_manage_rounds",
                                  e.target.checked,
                                )
                              }
                            >
                              จัดการรอบตรวจ
                            </Checkbox>
                          </Col>
                          <Col span={12}>
                            <Checkbox
                              checked={company.can_view_reports}
                              onChange={(e) =>
                                updateCompanyPermission(
                                  company.company_id,
                                  "can_view_reports",
                                  e.target.checked,
                                )
                              }
                            >
                              ดูรายงาน
                            </Checkbox>
                          </Col>
                          <Col span={12}>
                            <Checkbox
                              checked={company.can_export_data}
                              onChange={(e) =>
                                updateCompanyPermission(
                                  company.company_id,
                                  "can_export_data",
                                  e.target.checked,
                                )
                              }
                            >
                              ส่งออกข้อมูล
                            </Checkbox>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={2}>
                        <Tooltip
                          title={
                            originalCompanyIds.includes(company.company_id)
                              ? "บริษัทที่กำหนดสิทธิ์แล้วไม่สามารถลบได้"
                              : "ลบบริษัท"
                          }
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={originalCompanyIds.includes(
                              company.company_id,
                            )}
                            onClick={() =>
                              removeCompanyPermission(company.company_id)
                            }
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>

              {selectedCompanies.length === 0 && (
                <Empty
                  description="ยังไม่ได้เลือกบริษัท"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Alert
                message="ตรวจสอบข้อมูลก่อนบันทึก"
                type="success"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Card
                title="ข้อมูลผู้ดูแลระบบ"
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>ชื่อ-นามสกุล:</Text>
                    <br />
                    <Text>{form.getFieldValue("name")}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>อีเมล:</Text>
                    <br />
                    <Text>{form.getFieldValue("email")}</Text>
                  </Col>
                </Row>
              </Card>

              <Card
                title={`สิทธิ์การเข้าถึงบริษัท (${selectedCompanies.length} บริษัท)`}
                size="small"
              >
                {selectedCompanies.map((company) => (
                  <div
                    key={company.company_id}
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Row>
                      <Col span={8}>
                        <Text strong>{company.company_name}</Text>
                        <br />
                        <Tag color="blue">{company.access_level}</Tag>
                      </Col>
                      <Col span={16}>
                        <Space wrap>
                          {company.can_manage_users && (
                            <Tag color="green">จัดการผู้ใช้</Tag>
                          )}
                          {company.can_manage_checkpoints && (
                            <Tag color="green">จัดการจุดตรวจ</Tag>
                          )}
                          {company.can_manage_rounds && (
                            <Tag color="green">จัดการรอบตรวจ</Tag>
                          )}
                          {company.can_view_reports && (
                            <Tag color="green">ดูรายงาน</Tag>
                          )}
                          {company.can_export_data && (
                            <Tag color="green">ส่งออกข้อมูล</Tag>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </div>
                ))}
              </Card>
            </div>
          )}

          <Divider />

          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
              {currentStep > 0 && <Button onClick={prevStep}>ย้อนกลับ</Button>}
              {currentStep < 2 ? (
                <Button type="primary" onClick={nextStep}>
                  ถัดไป
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={() => handleSubmit(form.getFieldsValue(true))}
                >
                  {editingAdmin ? "บันทึกการแก้ไข" : "สร้างผู้ดูแลระบบ"}
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal สร้างผู้จัดการ รปภ */}
      <Modal
        title={
          <Space>
            <TeamOutlined />
            สร้างผู้จัดการ รปภ
          </Space>
        }
        open={patrolModalVisible}
        onCancel={() => setPatrolModalVisible(false)}
        footer={null}
        width={500}
        destroyOnClose
      >
        <Alert
          message="ผู้จัดการ รปภ จะเข้า Admin Panel ด้วย email + PIN เดิมเป็นรหัสผ่าน และยังใช้ Mobile App ได้ตามปกติ"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form form={patrolForm} layout="vertical" onFinish={handleSubmitPatrol}>
          <Form.Item
            name="user_id"
            label="เลือก รปภ"
            rules={[{ required: true, message: "กรุณาเลือก รปภ" }]}
          >
            <Select
              placeholder="เลือก รปภ ที่ต้องการ Promote"
              size="large"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {availableGuards.map((guard) => (
                <Option key={guard.id} value={guard.id}>
                  {guard.name} (PIN: {guard.employee_id || "-"}) - {guard.company_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="email"
            label="อีเมล (สำหรับ login Admin Panel)"
            rules={[
              { required: true, message: "กรุณาระบุอีเมล" },
              { type: "email", message: "รูปแบบอีเมลไม่ถูกต้อง" },
            ]}
          >
            <Input placeholder="email@company.com" size="large" />
          </Form.Item>

          <Divider />

          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setPatrolModalVisible(false)}>ยกเลิก</Button>
              <Button type="primary" htmlType="submit" loading={patrolCreating}>
                Promote เป็นผู้จัดการ รปภ
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal ดูสิทธิ์ */}
      <Modal
        title={`สิทธิ์การเข้าถึงของ ${editingAdmin?.name}`}
        open={permissionModalVisible}
        onCancel={() => setPermissionModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPermissionModalVisible(false)}>
            ปิด
          </Button>,
        ]}
        width={800}
      >
        <List
          dataSource={selectedCompanies}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Popconfirm
                  title="ต้องการเพิกถอนสิทธิ์การเข้าถึงบริษัทนี้?"
                  onConfirm={() =>
                    handleRevokeCompanyAccess(editingAdmin?.id, item.company_id)
                  }
                  okText="เพิกถอน"
                  cancelText="ยกเลิก"
                  okType="danger"
                >
                  <Button type="text" danger icon={<DeleteOutlined />}>
                    เพิกถอนสิทธิ์
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<BankOutlined />} />}
                title={item.company_name}
                description={
                  <Space direction="vertical" size={4}>
                    <Tag color="blue">{item.access_level}</Tag>
                    <Space wrap>
                      {item.can_manage_users === 1 && (
                        <Tag color="green">จัดการผู้ใช้</Tag>
                      )}
                      {item.can_manage_checkpoints === 1 && (
                        <Tag color="green">จัดการจุดตรวจ</Tag>
                      )}
                      {item.can_manage_rounds === 1 && (
                        <Tag color="green">จัดการรอบตรวจ</Tag>
                      )}
                      {item.can_view_reports === 1 && (
                        <Tag color="green">ดูรายงาน</Tag>
                      )}
                      {item.can_export_data === 1 && (
                        <Tag color="green">ส่งออกข้อมูล</Tag>
                      )}
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* Modal กำหนดผู้ใต้บังคับบัญชา */}
      <Modal
        title={`กำหนดผู้ใต้บังคับบัญชาของ ${editingSupervisor?.name || ""}`}
        open={subordinateModalVisible}
        onCancel={() => setSubordinateModalVisible(false)}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => setSubordinateModalVisible(false)}>
            ยกเลิก
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={subordinateSaving}
            onClick={handleSaveSubordinates}
          >
            บันทึก
          </Button>,
        ]}
      >
        <Alert
          message="เลือก รปภ ที่อยู่ภายใต้การดูแลของผู้จัดการคนนี้"
          description="รปภ ที่ถูกเลือกจะสามารถส่งคำขอหยุดเสียงเตือนถึงผู้จัดการคนนี้ได้"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Transfer
          dataSource={allGuardsForAssignment.map((g) => ({
            key: g.id.toString(),
            title: g.name,
            description: `${g.employee_id || ""} - ${g.company_name}`,
          }))}
          titles={["รปภ ทั้งหมด", "ผู้ใต้บังคับบัญชา"]}
          targetKeys={selectedGuardIds.map(String)}
          onChange={(targetKeys) =>
            setSelectedGuardIds(targetKeys.map(Number))
          }
          render={(item) => (
            <span>
              {item.title}{" "}
              <span style={{ color: "#999", fontSize: 11 }}>
                {item.description}
              </span>
            </span>
          )}
          listStyle={{ width: 280, height: 350 }}
          showSearch
          filterOption={(input, item) =>
            item.title.toLowerCase().includes(input.toLowerCase()) ||
            item.description.toLowerCase().includes(input.toLowerCase())
          }
        />
      </Modal>
    </div>
  );
};

export default MultiCompanyAdmin;
